import React, { useState, useRef, useImperativeHandle, forwardRef, useEffect } from "react";
import { FileText, Upload, Check, Download, Loader2, X } from "lucide-react";
import { projectService } from "../services/projectService";
import Toast from "@/components/ui/Toast";
import { BASE_URL, downloadFile } from "@/lib/API";

const BALANCE_DOC_ACCEPT =
  "application/pdf,image/png,image/jpeg,image/jpg,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.pdf,.png,.jpg,.jpeg,.doc,.docx";

export const ProjectDocumentsTab = forwardRef(({ project, onProjectUpdated, onStateChange }, ref) => {
  const [files, setFiles] = useState([]);
  const [docName, setDocName] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef(null);
  const formRef = useRef(null);

  const merged = project?.mergedDocuments || [];
  const balanceDone = Boolean(project?.documentsStageComplete);

  const canUpload = (() => {
    try {
      const userStr = localStorage.getItem('blum_user') || localStorage.getItem('venus_user') || localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : {};
      return ['owner', 'admin', 'designer', 'senior_designer'].includes((user.role || '').toLowerCase());
    } catch (err) {
      return false;
    }
  })();

  useEffect(() => {
    if (onStateChange) {
      onStateChange({
        hasFiles: files.length > 0,
        submitting,
        canUpload
      });
    }
  }, [files.length, submitting, canUpload, onStateChange]);

  useImperativeHandle(ref, () => ({
    submit: () => {
      if (formRef.current) {
        formRef.current.requestSubmit();
      }
    },
    isSubmitting: submitting,
    hasFiles: files.length > 0,
    canUpload: canUpload
  }));

  const handleFileChange = (e) => {
    const incoming = e.target.files ? Array.from(e.target.files) : [];
    if (incoming.length === 0) return;
    setFiles((prev) => {
      const map = new Map(prev.map((f) => [`${f.name}-${f.size}`, f]));
      for (const f of incoming) {
        const key = `${f.name}-${f.size}`;
        if (!map.has(key)) map.set(key, f);
      }
      return Array.from(map.values());
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFileAt = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!files.length) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      if (docName.trim()) fd.append("documentName", docName.trim());
      if (notes.trim()) fd.append("notes", notes.trim());
      const updated = await projectService.uploadBalanceDocuments(
        project._id,
        fd,
      );
      if (onProjectUpdated) onProjectUpdated(updated);
      setFiles([]);
      setDocName("");
      setNotes("");
    } catch (err) {
      setToast({
        message: err?.response?.data?.message || err.message || "Upload failed",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const onDownload = (doc) => {
    // Use the secure streaming endpoint for guaranteed high-fidelity downloads
    const streamUrl = `/verve-projects/documents/stream?filePath=${encodeURIComponent(doc.path)}`;
    downloadFile(streamUrl, doc.originalName);
  };

  return (
    <div className="flex flex-col gap-4 w-full animate-in fade-in duration-500">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
     

      {canUpload && (
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="rounded-xl border border-slate-200 bg-white p-4 flex flex-col gap-4"
        >
          <div className="flex flex-col gap-1">
            <span className="text-[12px] font-semibold text-slate-500">
              Select multiple PDFs or images to upload for this project.
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label
                className="text-[11px] font-bold text-slate-500"
                htmlFor="balance-doc-name"
              >
                Document Name / Title
              </label>
              <input
                id="balance-doc-name"
                type="text"
                value={docName}
                onChange={(e) => setDocName(e.target.value)}
                placeholder="e.g. Site Map, Completion Certificate"
                className="h-10 px-3 border border-slate-200 rounded-lg text-[12px] font-semibold text-slate-700 focus:outline-none focus:border-[#f97316] transition-colors"
              />
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-[11px] font-bold text-slate-500">Files</span>
              <input
                id="balance-doc-files-input"
                ref={fileInputRef}
                type="file"
                multiple
                accept={BALANCE_DOC_ACCEPT}
                onChange={handleFileChange}
                className="sr-only"
              />
              <label
                htmlFor="balance-doc-files-input"
                className="h-10 px-4 rounded-lg border-2 border-dashed border-slate-200 bg-slate-50/50 text-[12px] font-bold text-slate-600 inline-flex items-center justify-center gap-2 cursor-pointer transition-all duration-300 ease-in-out hover:border-[#f97316] hover:text-[#f97316]"
              >
                <Upload className="w-4 h-4 shrink-0" />
                Choose files
              </label>
            </div>
          </div>

          {files.length > 0 && (
            <div className="flex flex-col gap-2">
              <span className="text-[11px] font-semibold text-slate-500">
                {files.length} file{files.length === 1 ? "" : "s"} selected
              </span>
              <ul className="flex flex-wrap gap-2">
                {files.map((file, idx) => (
                  <li
                    key={`${file.name}-${file.size}-${idx}`}
                    className="flex items-center gap-2 pl-2 pr-1 py-1 rounded-lg border border-slate-200 bg-white max-w-full"
                  >
                    <span className="text-[11px] font-bold text-slate-700 truncate min-w-0">
                      {file.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFileAt(idx)}
                      className="h-6 w-6 shrink-0 inline-flex items-center justify-center rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-all duration-300 ease-in-out"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-col gap-2 w-full">
            <label
              className="text-[11px] font-bold text-slate-500"
              htmlFor="balance-doc-notes"
            >
              Notes (optional)
            </label>
            <textarea
              id="balance-doc-notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Signed document uploaded by PM"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[12px] font-semibold text-slate-700 focus:outline-none focus:border-[#f97316] transition-colors"
            />
          </div>
        </form>
      )}

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden flex flex-col">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-600" />
            <span className="text-[13px] font-bold text-slate-800">
              Uploaded documentation
            </span>
          </div>
          {balanceDone && (
            <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-md">
              Approved
            </span>
          )}
        </div>
        <div className="p-4 flex flex-col gap-3">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">
            Project documents
          </span>
          {merged.length === 0 ? (
            <p className="text-[12px] font-semibold text-slate-400">
              No documents uploaded yet.
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {merged.map((doc, i) => (
                <li
                  key={`${doc.path}-${i}`}
                  className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50/50 px-3 py-2"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                    <span className="text-[12px] font-bold text-slate-700 truncate">
                      {doc.originalName || "File"}
                    </span>
                    <span className="text-[10px] font-semibold text-slate-400 shrink-0 capitalize">
                      (
                      {doc.source === "balance_update"
                        ? "Balance update"
                        : "Launch"}
                      )
                    </span>
                  </div>
                  <button
                    onClick={() => onDownload(doc)}
                    className="h-9 w-9 shrink-0 inline-flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:text-[#2b448f] hover:border-[#2b448f] transition-all cursor-pointer"
                    title="Download"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {merged.some((d) => d.notes) && (
            <p className="text-[11px] font-semibold text-slate-500">
              Notes: {merged.find((d) => d.notes)?.notes}
            </p>
          )}
        </div>
      </div>
    </div>
  );
});
